import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import type { ReactElement, ReactNode } from 'react';
import styles from './styles.module.css';

interface ITooltipContentProps {
  /** Primary line. */
  title: ReactNode;
  /** Muted supporting lines, each on its own row. */
  lines?: ReactNode[];
  /**
   * The lines are separate GROUPS (e.g. what was booked, then where), not one
   * sentence broken up: each line after the first gets a little space and a
   * hairline above it. Without it a line that wraps runs straight into the
   * next and the two read as one paragraph.
   */
  divided?: boolean;
}

/**
 * Standard tooltip body — a primary line plus muted supporting lines, the shape
 * the portfolio milestone/progress popups established.
 *
 * Use this instead of composing `<b>` and `<br />` inline: the popup is a flex
 * column, so raw text nodes and `<br />` become anonymous flex items and space
 * out unevenly (an obvious gap under the first line).
 */
export function TooltipContent({ title, lines, divided = false }: ITooltipContentProps) {
  return (
    <>
      <span>{title}</span>
      {(lines ?? []).map((line, i) => (
        <span key={i} className={divided && i > 0 ? `${styles.contentSub} ${styles.contentGroup}` : styles.contentSub}>
          {line}
        </span>
      ))}
    </>
  );
}

interface IMarkerPopupProps {
  children: ReactNode;
}

/**
 * The navy `marker` popup without Base UI's hover machinery — for a chart
 * library that positions its own tooltip (Recharts' `<Tooltip content>`), so a
 * hovered bar reads like every other data mark.
 */
export function MarkerPopup({ children }: IMarkerPopupProps) {
  return <div className={styles.popupMarker}>{children}</div>;
}

interface ITooltipProps {
  content: ReactNode;     // Text/markup shown on hover. When empty, the trigger renders without a tooltip
  children: ReactElement; // The element the tooltip is anchored to (e.g. an icon glyph)
  delay?: number;         // Hover open delay in ms.
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  /**
   * Controlled open state. Use with `onOpenChange` when opening depends on more
   * than hover — e.g. only showing a name popup when the label is actually
   * clipped. Leave both unset for the normal uncontrolled behaviour.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * `hint` (default) — grey popup for explanatory text on an icon or label.
   * `marker` — navy popup for hovering a visual data element (a milestone
   * diamond, a progress bar, a floor chip), matching the portfolio
   * MilestoneStrip. See frontend/CLAUDE.md.
   * `card` — no surface of its own, for content that already is a card (a
   * delivery order previewed from a calendar chip).
   */
  variant?: 'hint' | 'marker' | 'card';
  /**
   * Let a `marker` popup wrap onto multiple lines (capped at 320px). Off by
   * default: a marker usually carries a short label, and wrapping one would
   * only make it taller. Turn it on when the content is sentences.
   * No effect on `hint`, which already wraps at 260px.
   */
  wrap?: boolean;
}

/**
 * Lightweight styled hover tooltip — a thin wrapper over Base UI's tooltip,
 * matching trusstor_dashboard's `TooltipedIcon`. Replaces the laggy, unstyled
 * native `title` for hover-only affordances.
 */
export function Tooltip({
  content,
  children,
  delay = 150,
  side = 'top',
  variant = 'hint',
  wrap = false,
  align = 'center',
  sideOffset = 4,
  open,
  onOpenChange,
}: ITooltipProps) {
  if (content == null || content === '') return children;
  return (
    // `disableHoverablePopup`: the popup is non-interactive (it just shows
    // text), so it should behave like the native `title` it replaces and close
    // as soon as the cursor leaves the trigger — not stay open when the cursor
    // moves onto the popup.
    <BaseTooltip.Root disableHoverablePopup open={open} onOpenChange={onOpenChange}>
      <BaseTooltip.Trigger delay={delay} render={children} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={styles.positioner}
        >
          <BaseTooltip.Popup
            className={
              variant === 'marker'
                ? `${styles.popupMarker}${wrap ? ` ${styles.popupWrap}` : ''}`
                : variant === 'card' ? styles.popupCard : styles.popup
            }
          >
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}
